// =============================================================================
// src/views/demografica/demografica-template.ts
//
// Marcacao FIEL da secao "04 · Analise Demografica".
//
// Proveniencia: unified.html linhas 671-846 (#section-demografica), copiada
//   1:1 — mesmas tags, classes, ids, atributos, textos e comentarios HTML.
//   Os onclick="sendPrompt('...')" inline seguem funcionando via a facade
//   global (window.sendPrompt), conforme o contrato de modulo.
//
// Regra Clean Code: arquivo de SRP unica (HTML literal). Excecao consciente a
//   regra de "sem comentarios obvios": os comentarios HTML sao do original e
//   carregam o PORQUE de cada banda — preservados intactos (rule 4).
// =============================================================================

import { buildProvenanceFooterMarkup } from '../../data/data-provenance';
/**
 * Devolve a string HTML exata da secao demografica para o `render()` do modulo.
 * Paridade total com unified.html#section-demografica (671-846).
 */
export function renderDemograficaSection(): string {
  return `
    <section class="u-section" id="section-demografica" data-section="demografica">
      <div class="u-section-header">
        <div class="u-section-title">Análise Demográfica</div>
        <div class="u-section-subtitle">Recorte do eleitorado por características socioeconômicas</div>
      </div>
      <div class="u-section-body">

        <!-- Recorte band — chips do recorte ativo + trigger do drawer
             "editar recorte". O badge de amostra desceu para a banda KPI,
             e a sidebar de filtros virou drawer (linhas mais abaixo). -->
        <div class="u-demo-recorte">
          <span class="u-demo-recorte-label">Recorte ativo</span>
          <span id="u-demo-active-tags" style="display:flex;gap:4px;flex-wrap:wrap">
            <span class="u-recorte-chip" data-g="gender">Feminino <span class="rm">×</span></span>
            <span class="u-recorte-chip" data-g="age">25-34 anos <span class="rm">×</span></span>
            <span class="u-recorte-chip" data-g="edu">Ens. Superior <span class="rm">×</span></span>
          </span>
          <button id="u-demo-clear" type="button"
                  style="border:none;background:transparent;color:#185FA5;font-size:10px;cursor:pointer;font-family:inherit;padding:2px 4px">
            limpar
          </button>
          <button id="u-demo-edit-trigger" type="button" class="u-demo-recorte-edit"
                  aria-expanded="false" aria-controls="u-demo-edit-drawer">
            <i class="ti ti-adjustments-horizontal"></i> editar recorte
          </button>
        </div>

        <!-- KPI band — eleitoras do recorte + share + concentração textual -->
        <div class="u-demo-kpi-band">
          <div class="u-demo-kpi">
            <div class="u-demo-kpi-num" id="u-demo-total">84 mil</div>
            <div class="u-demo-kpi-label">eleitoras no recorte</div>
          </div>
          <div class="u-demo-kpi">
            <div class="u-demo-kpi-num u-demo-kpi-num--sm" id="u-demo-share">5,9%</div>
            <div class="u-demo-kpi-label">do eleitorado total</div>
            <div class="u-demo-kpi-extra"><i class="ti ti-trending-up" aria-hidden="true"></i> concentração em <strong>CS</strong> e <strong>Sul</strong></div>
          </div>
        </div>

        <!-- Sobre-índice pill + nota de amostragem -->
        <div class="u-demo-overindex-row">
          <span class="u-demo-overindex-pill">
            <i class="ti ti-arrow-up-right"></i>
            <strong>3×</strong> sobre-índice no voto PT
          </span>
          <span class="u-demo-sample-note">
            <i class="ti ti-alert-triangle"></i> amostra 84 mil · baixa em 2 zonas
          </span>
        </div>

        <!-- Insight callout — herói visual da seção, padrão amber do
             DESIGN.md. Texto âncora vem de tela-06 (a "razão" da tela).
             A narrativa dinâmica controlada por _syncRecorte vai numa
             linha de feedback embaixo, preservando o impacto do hero. -->
        <div class="u-demo-insight">
          <div class="u-demo-insight-tag"><i class="ti ti-bulb"></i> insight gerado</div>
          <p class="u-demo-insight-body">Mulheres jovens com superior em Manaus são <strong>o único recorte onde PT supera 10%</strong>. Vácuo de centro-esquerda ainda não capturado; concentrado em CS e Sul (áreas universitárias).</p>
          <div class="u-demo-insight-feedback" id="u-demo-insight"></div>
          <button class="u-demo-insight-cta" type="button"
                  onclick="sendPrompt('Detalhar estratégia para mulheres jovens com superior em Manaus')">
            <i class="ti ti-target"></i> ver oportunidade tática <i class="ti ti-arrow-right"></i>
          </button>
        </div>

        <!-- Distribuição de votos — full-width, barras finalmente legíveis -->
        <div class="u-demo-vote">
          <div class="u-panel-section-label">Distribuição de votos</div>
          <div class="u-vote-row">
            <div class="u-vote-party"><div class="pdot" style="background:#1D9E75"></div> Avante</div>
            <div class="u-vote-bar-bg"><div class="u-vote-bar-fill" style="width:48%;background:#1D9E75"></div></div>
            <div class="u-vote-pct">48%</div>
            <div class="u-vote-delta flat">0pp</div>
          </div>
          <div class="u-vote-row">
            <div class="u-vote-party"><div class="pdot" style="background:#C8B400"></div> PL</div>
            <div class="u-vote-bar-bg"><div class="u-vote-bar-fill" style="width:30%;background:#C8B400"></div></div>
            <div class="u-vote-pct">30%</div>
            <div class="u-vote-delta down">-7pp</div>
          </div>
          <div class="u-vote-row">
            <div class="u-vote-party"><div class="pdot" style="background:#E24B4A"></div> PT</div>
            <div class="u-vote-bar-bg"><div class="u-vote-bar-fill" style="width:12%;background:#E24B4A"></div></div>
            <div class="u-vote-pct">12%</div>
            <div class="u-vote-delta up">+8pp</div>
          </div>
          <div class="u-vote-row">
            <div class="u-vote-party"><div class="pdot" style="background:var(--color-text-tertiary)"></div> Outros</div>
            <div class="u-vote-bar-bg"><div class="u-vote-bar-fill" style="width:10%;background:var(--color-text-tertiary)"></div></div>
            <div class="u-vote-pct">10%</div>
            <div class="u-vote-delta down">-1pp</div>
          </div>
          <div class="u-demo-vote-foot">vs total geral de Manaus</div>
        </div>

        <!-- CTAs empilhados: primário em block + link ghost para exportar -->
        <div class="u-demo-cta">
          <button class="u-cta-btn u-cta-btn--block"
                  onclick="sendPrompt('Criar estratégia para eleitoras 25-34 ensino superior')">
            <i class="ti ti-target"></i> criar estratégia
          </button>
          <button class="u-demo-cta-link" type="button"
                  onclick="sendPrompt('Exportar análise demográfica')">
            <i class="ti ti-download"></i> exportar análise <i class="ti ti-arrow-up-right"></i>
          </button>
        </div>

        <!-- Drawer "Editar recorte" — todos os 6 grupos de filtros em
             vertical stack, escondido por default. Os bindings de chip e
             clear de Sections.demografica (.u-filter-group .u-chip) seguem
             válidos porque a hierarquia descendente é preservada. -->
        <div class="u-demo-edit-drawer" id="u-demo-edit-drawer" hidden>
          <div class="u-demo-edit-head">
            <span class="u-panel-section-label">Editar recorte</span>
            <button class="u-demo-edit-close" type="button" aria-label="Fechar editor de recorte">
              <i class="ti ti-x"></i>
            </button>
          </div>
          <div class="u-filter-group">
            <div class="u-filter-label"><i class="ti ti-gender-bigender"></i> Gênero</div>
            <div class="u-chips-row">
              <span class="u-chip" data-g="gender">Masculino</span>
              <span class="u-chip selected" data-g="gender">Feminino</span>
              <span class="u-chip" data-g="gender">Não-binário</span>
            </div>
          </div>
          <div class="u-filter-group">
            <div class="u-filter-label"><i class="ti ti-calendar"></i> Idade</div>
            <div class="u-chips-row">
              <span class="u-chip" data-g="age">18-24</span>
              <span class="u-chip selected" data-g="age">25-34</span>
              <span class="u-chip" data-g="age">35-44</span>
              <span class="u-chip" data-g="age">45-59</span>
              <span class="u-chip" data-g="age">60+</span>
            </div>
          </div>
          <div class="u-filter-group">
            <div class="u-filter-label"><i class="ti ti-school"></i> Escolaridade</div>
            <div class="u-chips-row">
              <span class="u-chip" data-g="edu">Fund.</span>
              <span class="u-chip" data-g="edu">Médio</span>
              <span class="u-chip selected" data-g="edu">Superior</span>
              <span class="u-chip" data-g="edu">Pós</span>
            </div>
          </div>
          <div class="u-filter-group">
            <div class="u-filter-label"><i class="ti ti-currency-dollar"></i> Renda</div>
            <div class="u-chips-row">
              <span class="u-chip" data-g="income">até 1SM</span>
              <span class="u-chip" data-g="income">1-3 SM</span>
              <span class="u-chip" data-g="income">3-5 SM</span>
              <span class="u-chip" data-g="income">5+ SM</span>
            </div>
          </div>
          <div class="u-filter-group">
            <div class="u-filter-label"><i class="ti ti-users"></i> Cor/Raça</div>
            <div class="u-chips-row">
              <span class="u-chip" data-g="race">Branca</span>
              <span class="u-chip" data-g="race">Parda</span>
              <span class="u-chip" data-g="race">Preta</span>
              <span class="u-chip" data-g="race">Indígena</span>
            </div>
          </div>
          <div class="u-filter-group">
            <div class="u-filter-label"><i class="ti ti-building-church"></i> Religião</div>
            <div class="u-chips-row">
              <span class="u-chip" data-g="rel">Católica</span>
              <span class="u-chip" data-g="rel">Evangélica</span>
              <span class="u-chip" data-g="rel">Sem religião</span>
              <span class="u-chip" data-g="rel">Outras</span>
            </div>
          </div>
        </div>

      </div>
      ${buildProvenanceFooterMarkup('demografica')}
    </section>`;
}
